import React, { useEffect, useState } from 'react';
import { FaQuestionCircle } from 'react-icons/fa';
import { getUserDocPath } from '../../tools/U2000GetUserDocPath';

export interface U2000HelpIconProps {
  /**
   * Code de l'unité logique (ex: "u3101")
   */
  logicalUnitCode: string;
  
  /**
   * Classes CSS supplémentaires
   */
  className?: string;
  
  /**
   * Taille de l'icône (en pixels, défaut: 20)
   */
  size?: number;
  
  /**
   * Titre de l'icône (tooltip)
   */
  title?: string;
}

interface DocManifest {
  version: string;
  generatedAt: string;
  logicalUnits: string[];
}

// Cache du manifest pour éviter de le charger plusieurs fois
let manifestCache: DocManifest | null = null;
let manifestLoading: Promise<DocManifest | null> | null = null;

/**
 * Charge le manifest des documentations disponibles
 */
async function loadManifest(): Promise<DocManifest | null> {
  // Si déjà en cache, retourner le cache
  if (manifestCache) {
    return manifestCache;
  }
  
  // Si déjà en cours de chargement, attendre
  if (manifestLoading) {
    return manifestLoading;
  }
  
  // Charger le manifest
  manifestLoading = fetch('/assets/docs/manifest.json')
    .then((response) => {
      if (!response.ok) {
        return null;
      }
      return response.json() as Promise<DocManifest>;
    })
    .then((manifest) => {
      manifestCache = manifest;
      return manifest;
    })
    .catch(() => {
      return null;
    })
    .finally(() => {
      manifestLoading = null;
    });
  
  return manifestLoading;
}

/**
 * Composant affichant une icône d'aide qui ouvre la documentation utilisateur
 * de l'unité logique dans un nouvel onglet.
 * L'icône n'est affichée que si la documentation existe.
 */
const U2000HelpIcon: React.FC<U2000HelpIconProps> = ({
  logicalUnitCode,
  className = '',
  size = 20,
  title = 'Documentation utilisateur',
}) => {
  const docPath = getUserDocPath(logicalUnitCode);
  const [docExists, setDocExists] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(true);
  
  useEffect(() => {
    if (!docPath || !logicalUnitCode) {
      setIsChecking(false);
      return;
    }
    
    // Charger le manifest et vérifier si l'unité logique a une documentation
    loadManifest()
      .then((manifest) => {
        if (manifest && manifest.logicalUnits) {
          const normalizedCode = logicalUnitCode.toLowerCase();
          const exists = manifest.logicalUnits.some(
            (lu) => lu.toLowerCase() === normalizedCode
          );
          setDocExists(exists);
        } else {
          setDocExists(false);
        }
        setIsChecking(false);
      })
      .catch(() => {
        setDocExists(false);
        setIsChecking(false);
      });
  }, [docPath, logicalUnitCode]);
  
  // Ne rien afficher si pas de chemin, si on vérifie encore, ou si la doc n'existe pas
  if (!docPath || isChecking || !docExists) {
    return null;
  }
  
  return (
    <a
      href={docPath}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors ${className}`}
      title={title}
      aria-label={title}
    >
      <FaQuestionCircle size={size} />
    </a>
  );
};

export default U2000HelpIcon;
